import { useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';

import type {
  ChartType,
  ExtendedOptions,
  ExtendedSeriesOptionsType
} from '@/components/Chart/types/highcharts';
import { EmptyState } from '@/components/EmptyState';
import Loader from '@/components/Loader';
import { DEFAULT_CHART_HEIGHT, TABLE_ROW_HEIGHT_PX } from '@/constants/constants';

import LegendDropdown from './LegendDropdown';
import useRegularChart from './useRegularChart';

const RegularChart = ({
  chartSeries,
  height,
  error,
  loading,
  showLegend = true,
  options,
  chartType = 'spot'
}: {
  chartSeries: ExtendedSeriesOptionsType[] | undefined;
  height?: number;
  error: boolean;
  loading: boolean;
  showLegend?: boolean;
  options: ExtendedOptions | undefined;
  chartType?: ChartType;
}) => {
  const { chart, handleChartRef, chartContainerRef } = useRegularChart({
    chartSeries
  });

  const chartContainerHeight = useMemo(
    () => (height ? height * TABLE_ROW_HEIGHT_PX : DEFAULT_CHART_HEIGHT),
    [height]
  );
  if (error) {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          height: chartContainerHeight,
          overflowX: 'hidden'
        }}
      >
        <Typography color='info'>
          We couldn't retrieve your data. Please try again later.
        </Typography>
      </Box>
    );
  }
  if (!loading && !chartSeries?.length) {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          height: chartContainerHeight,
          overflowX: 'hidden'
        }}
      >
        <EmptyState />
      </Box>
    );
  }

  if (!loading && !chartSeries?.length) {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          height: chartContainerHeight,
          overflowX: 'hidden'
        }}
      >
        <EmptyState />
      </Box>
    );
  }
  if (loading || !chartSeries?.length) {
    return (
      <Box sx={{ height: chartContainerHeight }}>
        <Loader />
      </Box>
    );
  }
  return (
    <Box
      ref={chartContainerRef}
      sx={{
        width: '100%',
        overflowX: 'hidden'
      }}
    >
      <Box className={chartType === 'spot' ? 'regular-chart' : 'base-chart'}>
        <HighchartsReact
          ref={handleChartRef}
          constructorType={chartType === 'spot' ? 'stockChart' : 'chart'}
          highcharts={Highcharts}
          options={options}
        />
        {chart && showLegend && (
          <LegendDropdown
            chart={chart}
            options={options}
          />
        )}
      </Box>
    </Box>
  );
};
export default RegularChart;
